import { createRoot } from 'react-dom/client';

import { Provider as ReduxProvider } from 'react-redux';
import DataInitializer from '@copilot/common/utils/dataInitializer/dataInitializerProvider';
import { Config } from '@copilot/common/config';
import Store from './store';
import TeamsRouter from './router/teamsRouter';
import { appInsights } from '@copilot/common/components/snippets/applicationInsights';
import { PermissionsProvider } from '@copilot/common/hooks/permission';
import AppDataInitializer from '@copilot/common/utils/dataInitializer/app';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { IntercomProvider } from 'react-use-intercom';
import ErrorBoundary from '@copilot/common/components/containers/errorBoundary';
import AppcuesProvider from '@copilot/common/components/snippets/appcues';
import AuthenticationProvider from '@copilot/common/utils/authenticationProvider';
import Apollo from '@copilot/common/components/snippets/apollo';
import MixpanelProvider from '@copilot/common/components/snippets/mixpanel/mixpanel';
import { SurvicateProvider } from '@copilot/common/components/snippets/survicate/provider';
import { Routes, Route, BrowserRouter } from 'react-router-dom';
import { OAuthPopup } from '@copilot/common/utils/oauth';
import CopilotThemeProvider from '../copilot-theme-provider';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import { LAUNCH_DARKLY_ID, WELL_KNOWN_TENANT } from '@copilot/data/config/routes';
import ClarityProvider from '@copilot/common/components/snippets/clarity';

const container = document.getElementById('root');
if (!container) throw new Error('Failed to find the root element');

const root = createRoot(container);

root.render(
    <ReduxProvider store={Store.ReduxStore}>
        <LDProvider
            clientSideID={LAUNCH_DARKLY_ID}
            context={{
                key: 'anonymous',
                custom: {
                    well_known_tenant: WELL_KNOWN_TENANT,
                },
            }}
        >
            <BrowserRouter>
                <Routes>
                    <Route path="/verify" element={<OAuthPopup />} />
                    <Route
                        path="*"
                        element={
                            <CopilotThemeProvider>
                                <AppInsightsContext.Provider value={appInsights.reactPlugin}>
                                    <IntercomProvider appId={Config.intercomAppId} autoBoot>
                                        <AuthenticationProvider>
                                            <AppDataInitializer>
                                                <Apollo>
                                                    <PermissionsProvider>
                                                        <DataInitializer isTeamUser>
                                                            <ClarityProvider
                                                                projectId={Config.clarityProjectId}
                                                                isEnabled={Config.isClarityEnabled}
                                                            >
                                                                <MixpanelProvider
                                                                    isEnabled={
                                                                        Config.isMixpanelEnabled
                                                                    }
                                                                    token={Config.mixpanelToken}
                                                                >
                                                                    <SurvicateProvider
                                                                        isEnabled={
                                                                            Config.isSurvicateEnabled
                                                                        }
                                                                        workspaceKey={
                                                                            Config.survicateKey
                                                                        }
                                                                    >
                                                                        <AppcuesProvider
                                                                            appId={
                                                                                Config.appcuesAppId
                                                                            }
                                                                            isEnabled={
                                                                                Config.isAppcuesEnabled
                                                                            }
                                                                        >
                                                                            <ErrorBoundary message="Something went wrong. Please refresh the page and try again">
                                                                                <TeamsRouter />
                                                                            </ErrorBoundary>
                                                                        </AppcuesProvider>
                                                                    </SurvicateProvider>
                                                                </MixpanelProvider>
                                                            </ClarityProvider>
                                                        </DataInitializer>
                                                    </PermissionsProvider>
                                                </Apollo>
                                            </AppDataInitializer>
                                        </AuthenticationProvider>
                                    </IntercomProvider>
                                </AppInsightsContext.Provider>
                            </CopilotThemeProvider>
                        }
                    />
                </Routes>
            </BrowserRouter>
        </LDProvider>
    </ReduxProvider>
);
